import React from "react";
import ReactDOM from "react-dom/client";
import { createHashRouter, RouterProvider } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import { HelmetProvider } from "react-helmet-async";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import { UserManager } from "oidc-client-ts";

import "./index.css";

import Chat from "./pages/chat/Chat";
import LayoutWrapper from "./layoutWrapper";
import { Component as ErrorPage } from "./pages/ErrorPage";
import i18next from "./i18n/config";
import { createUserManager, useLogin } from "./authConfig";
import { OidcContext } from "./oidcContext";

const router = createHashRouter([
    {
        path: "/",
        element: <LayoutWrapper />,
        // Router-level fallback: replaces the raw "Unexpected Application Error!"
        // screen with a friendly page + reload action (e.g. stale bundle after deploy).
        errorElement: <ErrorPage />,
        children: [
            {
                index: true,
                element: <Chat />
            },
            {
                path: "*",
                lazy: () => import("./pages/NoPage")
            }
        ]
    }
]);

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

// Bootstrap the app; create the OIDC UserManager if login is enabled and handle the callback
(async () => {
    let userManager: UserManager | undefined;

    if (useLogin) {
        try {
            userManager = createUserManager();

            // Handle popup callback: if we're in a popup window with an auth code, complete the flow and stop
            if (window.opener && window.location.search.includes("code=")) {
                await userManager.signinPopupCallback();
                return;
            }

            // Handle redirect callback after Keycloak login (code= in URL)
            if (window.location.search.includes("code=") || window.location.hash.includes("code=")) {
                await userManager.signinRedirectCallback();
                // Remove code/state from URL after successful callback
                window.history.replaceState({}, document.title, window.location.pathname + window.location.hash.split("?")[0]);
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error("OIDC initialization failed", e);
            // Clean up URL and redirect to home so the app doesn't show a blank page
            window.history.replaceState({}, document.title, "/");
        }
    }

    const appTree = (
        <React.StrictMode>
            <FluentProvider theme={webLightTheme}>
                <I18nextProvider i18n={i18next}>
                    <HelmetProvider>
                        <OidcContext.Provider value={{ userManager }}>
                            <RouterProvider router={router} />
                        </OidcContext.Provider>
                    </HelmetProvider>
                </I18nextProvider>
            </FluentProvider>
        </React.StrictMode>
    );

    root.render(appTree);
})();
